/**
 * As permissões por pessoa (ADR-0034).
 *
 * O catálogo do que se liga (#727): as abas, os assuntos e as ações que a
 * seção Permissões desenha, e o padrão de cada papel da organização. E as
 * pessoas com as permissões delas, que a seção lê e salva (#1190). Os nomes de
 * recurso e de ação são os da matriz da API; o teste
 * `apps/api/src/permissoes/__tests__/catalogo.test.mts` reprova se as duas
 * listas divergirem.
 */

import { z } from 'zod';
import { CarteiraSchema, IdSchema, PapelSchema } from './comum.js';

/** Os recursos da matriz de permissões. */
export const RecursoDePermissaoSchema = z.enum([
  'conversa',
  'mensagem',
  'cliente',
  'oportunidade',
  'tarefa',
  'usuario',
  'time',
  'integracao',
  'automacao',
  'integracao_de_dados',
  'lista',
  'indicador',
  'auditoria',
  'configuracao',
  'campo_personalizado',
  'mensagem_pronta',
  'permissao',
  'titular',
]);

export type RecursoDePermissao = z.infer<typeof RecursoDePermissaoSchema>;

/** As ações da matriz. */
export const AcaoDePermissaoSchema = z.enum([
  'ler',
  'criar',
  'editar',
  'remover',
  'atribuir',
  'exportar',
  'ativar',
]);

export type AcaoDePermissao = z.infer<typeof AcaoDePermissaoSchema>;

/** Os papéis que a organização dá às pessoas dela: um recorte do `PapelSchema`. */
export const PapelDaOrganizacaoSchema = PapelSchema.extract([
  'agente',
  'vendedor',
  'gestor',
  'admin_tenant',
]);

export type PapelDaOrganizacao = z.infer<typeof PapelDaOrganizacaoSchema>;

const PreRequisitoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  acao: AcaoDePermissaoSchema,
});

const AcaoDoCatalogoSchema = z.object({
  acao: AcaoDePermissaoSchema,
  /** O texto do toggle. */
  rotulo: z.string(),
  /** A linha de apoio embaixo do toggle. */
  descricao: z.string().nullable(),
  /**
   * O que precisa estar ligado para esta ligar; desligar um deles desliga
   * esta. A de ver do próprio assunto, e às vezes a de outro (responder pede
   * ver as conversas).
   */
  depende_de: z.array(PreRequisitoSchema),
  /** O motivo de aparecer travada (é só da Cliqfy), ou `null`. */
  trava: z.string().nullable(),
});

const AssuntoDoCatalogoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  nome: z.string(),
  cobre: z.string(),
  /** Tem "Até onde vale". Os outros valem para a organização inteira. */
  tem_alcance: z.boolean(),
  acoes: z.array(AcaoDoCatalogoSchema),
});

const PermissaoDoAssuntoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  acoes: z.array(AcaoDePermissaoSchema),
  /** Até onde vale: os mesmos níveis da carteira do vínculo. */
  alcance: CarteiraSchema,
});

/**
 * O que a pessoa pode num assunto, como o `/v1/me` entrega (#732): as ações e
 * até onde valem. O alcance é o da carteira, ou `nenhuma` no assunto em que a
 * pessoa não tem ação nenhuma.
 */
export const PermissaoDaSessaoSchema = z.object({
  recurso: RecursoDePermissaoSchema,
  acoes: z.array(AcaoDePermissaoSchema),
  alcance: z.enum(['nenhuma', 'propria', 'time', 'unidade', 'tenant']),
});

export const CatalogoDePermissoesSchema = z.object({
  abas: z.array(
    z.object({
      aba: z.enum(['atendimento', 'comercial', 'organizacao', 'integracoes']),
      rotulo: z.string(),
      assuntos: z.array(AssuntoDoCatalogoSchema),
    }),
  ),
  /** O padrão de cada papel: o que ele pode hoje, e de onde parte um ajuste. */
  padroes: z.array(
    z.object({
      papel: PapelDaOrganizacaoSchema,
      permissoes: z.array(PermissaoDoAssuntoSchema),
    }),
  ),
});

export type CatalogoDePermissoes = z.infer<typeof CatalogoDePermissoesSchema>;

/**
 * Uma pessoa na seção Permissões (#1190). O estado é "padrão do papel" ou
 * "personalizada" (tem ajuste). O Suporte Cliqfy, o Super Admin e a conta
 * Automação não aparecem: só a Cliqfy altera esses acessos.
 */
export const PessoaDasPermissoesSchema = z.object({
  /** O id do vínculo da pessoa na organização, o mesmo das rotas de pessoas. */
  id: IdSchema,
  nome: z.string(),
  email: z.string(),
  papel: PapelDaOrganizacaoSchema,
  carteira: CarteiraSchema,
  estado: z.enum(['padrao', 'personalizada']),
});

export const PessoasDasPermissoesSchema = z.object({
  itens: z.array(PessoaDasPermissoesSchema),
});

/** Os ajustes como ficam guardados: só o que difere do padrão do papel. */
const AjustesSchema = z.object({
  /** `recurso:ação` ligada (`true`) além do papel ou tirada (`false`) dele. */
  acoes: z.record(z.string(), z.boolean()),
  /** recurso -> até onde vale, nos assuntos com "Até onde vale". */
  alcances: z.record(z.string(), CarteiraSchema),
});

/** As permissões de uma pessoa, na tela dela (#1190). */
export const PermissoesDaPessoaSchema = z.object({
  pessoa: PessoaDasPermissoesSchema,
  /** O padrão do papel: de onde partem os selos "Além do papel" e "Tirada do papel". */
  padrao: z.array(PermissaoDaSessaoSchema),
  /** O que ela pode hoje, com os ajustes e as dependências do catálogo. */
  efetivas: z.array(PermissaoDaSessaoSchema),
  ajustes: AjustesSchema,
  /**
   * Quem está na tela pode salvar? `limitada` é quem só tem "Gerenciar
   * permissões": só liga o que tem, até o alcance dele. `motivo` diz por que
   * não pode, quando não pode.
   */
  edicao: z.object({
    pode: z.boolean(),
    motivo: z.string().nullable(),
    limitada: z.boolean(),
  }),
});

/**
 * Salvar as permissões de uma pessoa: o que a barra "Salvar alterações" manda.
 * Os ajustes vazios são o "Voltar ao padrão do papel". `papel` é o que a tela
 * mostrava: se ele mudou no meio, a gravação é recusada (409), para um ajuste
 * escolhido sobre um papel não valer sobre outro.
 */
export const SalvarPermissoesDaPessoaSchema = z
  .object({
    papel: PapelDaOrganizacaoSchema,
    acoes: z.record(z.string(), z.boolean()),
    alcances: z.record(z.string(), z.string()),
  })
  .strict();

export type PessoaDasPermissoes = z.infer<typeof PessoaDasPermissoesSchema>;
export type PessoasDasPermissoes = z.infer<typeof PessoasDasPermissoesSchema>;
export type PermissoesDaPessoa = z.infer<typeof PermissoesDaPessoaSchema>;
export type SalvarPermissoesDaPessoa = z.infer<typeof SalvarPermissoesDaPessoaSchema>;
